import { Box, CircularProgress, LinearProgress, Skeleton, Typography } from '@mui/material';
import React from 'react';

interface LoadingAnimationProps {
  /**
   * Type of loading animation to display
   * - 'spinner': Circular spinner (default)
   * - 'linear': Linear progress bar
   * - 'skeleton': Skeleton placeholder
   * - 'dots': Animated dots
   */
  variant?: 'spinner' | 'linear' | 'skeleton' | 'dots';
  
  /**
   * Size of the loading indicator
   * - 'small': 20px
   * - 'medium': 40px (default)
   * - 'large': 60px
   */
  size?: 'small' | 'medium' | 'large';
  
  /**
   * Optional message to display below the loading indicator
   */
  message?: string;
  
  /**
   * Whether to center the loading indicator (default: true)
   */
  centered?: boolean;
  
  /**
   * Custom color for the loading indicator
   */
  color?: 'primary' | 'secondary' | 'inherit';
  
  /**
   * Full page loading overlay (default: false)
   */
  fullPage?: boolean;
  
  /**
   * Minimum height for the loading container
   */
  minHeight?: string | number;
  
  /**
   * Aria label for accessibility
   */
  ariaLabel?: string;
}

const SIZES = {
  small: 20,
  medium: 40,
  large: 60,
};

/**
 * Reusable loading animation component - mobile-friendly and accessible
 * 
 * Usage examples:
 * ```tsx
 * // Default spinner
 * <LoadingAnimation />
 * 
 * // With message
 * <LoadingAnimation message="Loading training plan..." />
 * 
 * // Full page overlay
 * <LoadingAnimation fullPage message="Processing..." />
 * 
 * // Linear progress bar
 * <LoadingAnimation variant="linear" />
 * 
 * // Skeleton loader
 * <LoadingAnimation variant="skeleton" />
 * ```
 */
export const LoadingAnimation: React.FC<LoadingAnimationProps> = ({
  variant = 'spinner',
  size = 'medium',
  message,
  centered = true,
  color = 'primary',
  fullPage = false,
  minHeight,
  ariaLabel = 'Loading',
}) => {
  const spinnerSize = SIZES[size];

  const containerStyles = {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: centered ? 'center' : 'flex-start',
    justifyContent: centered ? 'center' : 'flex-start',
    gap: 2,
    minHeight: minHeight || (centered ? '200px' : 'auto'),
    width: '100%',
    ...(fullPage && {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      backdropFilter: 'blur(4px)',
      zIndex: 9999,
      minHeight: '100vh',
    }),
  };

  const renderLoadingIndicator = () => {
    switch (variant) {
      case 'linear':
        return (
          <Box sx={{ width: '100%', maxWidth: 400 }}>
            <LinearProgress color={color} aria-label={ariaLabel} />
          </Box>
        );

      case 'skeleton':
        return (
          <Box sx={{ width: '100%', maxWidth: 400 }}>
            <Skeleton variant="rectangular" height={60} sx={{ mb: 1 }} />
            <Skeleton variant="text" sx={{ fontSize: '1rem', mb: 0.5 }} />
            <Skeleton variant="text" sx={{ fontSize: '1rem', width: '80%' }} />
          </Box>
        );

      case 'dots':
        return (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
            }}
            role="status"
            aria-label={ariaLabel}
          >
            {[0, 1, 2].map(i => (
              <Box
                key={i}
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: color === 'primary' ? 'primary.main' : 'secondary.main',
                  animation: 'pulse 1.4s ease-in-out infinite',
                  animationDelay: `${i * 0.2}s`,
                  '@keyframes pulse': {
                    '0%, 80%, 100%': {
                      transform: 'scale(0.8)',
                      opacity: 0.5,
                    },
                    '40%': {
                      transform: 'scale(1.2)',
                      opacity: 1,
                    },
                  },
                }}
              />
            ))}
          </Box>
        );

      case 'spinner':
      default:
        return (
          <CircularProgress
            size={spinnerSize}
            color={color}
            aria-label={ariaLabel}
            role="status"
          />
        );
    }
  };

  return (
    <Box sx={containerStyles}>
      {renderLoadingIndicator()}
      {message && (
        <Typography
          variant="body2"
          color="text.secondary"
          textAlign={centered ? 'center' : 'left'}
          sx={{
            maxWidth: 400,
            px: 2,
          }}
        >
          {message}
        </Typography>
      )}
    </Box>
  );
};

/**
 * Inline loading indicator - for use inside buttons or small spaces
 */
export const InlineLoader: React.FC<{ size?: number; color?: string }> = ({
  size = 16,
  color,
}) => (
  <CircularProgress
    size={size}
    sx={{ color: color || 'inherit' }}
    aria-label="Loading"
    role="status"
  />
);

/**
 * Button loading state wrapper - adds loading spinner to button content
 */
interface LoadingButtonContentProps {
  isLoading: boolean;
  loadingText?: string;
  children: React.ReactNode;
}

export const LoadingButtonContent: React.FC<LoadingButtonContentProps> = ({
  isLoading,
  loadingText,
  children,
}) => {
  if (!isLoading) return <>{children}</>;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <InlineLoader size={16} />
      {loadingText || children}
    </Box>
  );
};
